import { appSwitchRoutes, getRoleHomeUrl } from 'constants/routes';
import {
  MyRoleStageActionsQuery,
  StageCandidateStatus,
  useMyRoleStageActionsQuery,
} from 'generated/graphql';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';

export type TMyRole = MyRoleStageActionsQuery['myRole'];
export type TMyRoleStage = TMyRole['stages'][number];

/**
 * Stage-candidate statuses under which the candidate can still act on the
 * stage — anything else bounces back to the role's stage list.
 */
export const isStageActionable = (stage: TMyRoleStage): boolean =>
  stage.candidateStatus === StageCandidateStatus.NotStarted ||
  stage.candidateStatus === StageCandidateStatus.InProgress;

/**
 * Shared plumbing for the role-scoped action flows living under
 * `/roles/:roleId/stages/:stageId/actions/...`: loads the stage (with its
 * actions) and owns the common exit guards. Exits go to the role's home
 * screen (portal when enabled, stage list otherwise) — except query errors,
 * which fall back to the stage list because the flag never loaded. A stage
 * whose candidate is no longer actionable also exits, unless
 * `exitWhenNotActionable` is disabled (the thank-you page shows the
 * congratulations panel in exactly that state).
 */
export const useRoleStageActionsFlow = (
  { exitWhenNotActionable }: { exitWhenNotActionable: boolean } = {
    exitWhenNotActionable: true,
  }
) => {
  const navigate = useNavigate();
  const { handleMsgType } = useNotification();
  const { roleId, stageId } = useParams();

  const roleStagesUrl = appSwitchRoutes.role.getUrl(roleId as string);
  const actionsUrl = appSwitchRoutes.roleStageActions.getUrl(
    roleId as string,
    stageId as string
  );
  const thankYouUrl = appSwitchRoutes.roleStageThankYou.getUrl(
    roleId as string,
    stageId as string
  );

  const queryResponse = useMyRoleStageActionsQuery({
    variables: {
      roleId: roleId as string,
      stageId: stageId as string,
    },
    skip: !roleId || !stageId,
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
      });

      navigate(roleStagesUrl);
    },
    fetchPolicy: 'network-only',
  });

  const role = queryResponse.data?.myRole;
  const stage = role?.stages[0];

  const roleHomeUrl = getRoleHomeUrl(
    roleId as string,
    role?.candidatePortalEnabled
  );

  const shouldExitToRoleHome =
    !!queryResponse.data &&
    (!stage || (exitWhenNotActionable && !isStageActionable(stage)));

  useEffect(() => {
    if (shouldExitToRoleHome) {
      navigate(roleHomeUrl);
    }
  }, [shouldExitToRoleHome, navigate, roleHomeUrl]);

  return {
    roleId: roleId as string,
    stageId: stageId as string,
    role,
    stage,
    loading: queryResponse.loading,
    /** True while an exit-guard navigation is pending — render nothing. */
    exiting: shouldExitToRoleHome,
    refetch: queryResponse.refetch,
    roleHomeUrl,
    actionsUrl,
    thankYouUrl,
  };
};
